<template>
  <el-card>
    <el-form :inline="true" @submit.prevent>
      <el-form-item>
        <el-input v-model.trim="keyword" placeholder="关键词/备注" @keyup.enter="onSearch" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="onSearch">搜索</el-button>
        <el-button @click="onReset">重置</el-button>
        <el-button @click="openCreate">新增规则</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" stripe border v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="keyword" label="关键词" min-width="160" />
      <el-table-column label="禁文字" width="100">
        <template #default="{ row }">
          <el-tag :type="row.ban_text ? 'danger' : 'info'">{{ row.ban_text ? '是' : '否' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="禁语音" width="100">
        <template #default="{ row }">
          <el-tag :type="row.ban_audio ? 'warning' : 'info'">{{ row.ban_audio ? '是' : '否' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="启用" width="100">
        <template #default="{ row }">
          <el-tag :type="row.enabled ? 'success' : 'info'">{{ row.enabled ? '启用' : '停用' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="note" label="备注" min-width="220" show-overflow-tooltip />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-space>
            <el-button link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-button link type="danger" @click="remove(row.id)">删除</el-button>
          </el-space>
        </template>
      </el-table-column>
    </el-table>

    <div style="margin-top: 12px; display: flex; justify-content: flex-end;">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="total"
        :current-page="query.page"
        :page-size="query.page_size"
        @current-change="onPageChange"
      />
    </div>
  </el-card>

  <el-dialog v-model="dialogVisible" :title="editId ? '编辑关键词规则' : '新增关键词规则'" width="520px">
    <el-form label-position="top">
      <el-form-item label="关键词">
        <el-input v-model.trim="form.keyword" placeholder="例如：点击右上角" />
      </el-form-item>
      <el-form-item label="拦截范围">
        <el-space>
          <el-checkbox v-model="form.ban_text">禁止文字返回</el-checkbox>
          <el-checkbox v-model="form.ban_audio">禁止语音返回</el-checkbox>
        </el-space>
      </el-form-item>
      <el-form-item label="是否启用">
        <el-switch v-model="form.enabled" />
      </el-form-item>
      <el-form-item label="备注">
        <el-input v-model.trim="form.note" type="textarea" :rows="3" placeholder="规则说明（可选）" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" @click="save">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { adminApi } from '../api/admin'

const keyword = ref('')
const list = ref([])
const total = ref(0)
const loading = ref(false)
const query = ref({ page: 1, page_size: 20 })
const dialogVisible = ref(false)
const editId = ref(0)
const form = ref({
  keyword: '',
  ban_text: true,
  ban_audio: false,
  enabled: true,
  note: ''
})

async function load() {
  loading.value = true
  try {
    const data = await adminApi.keywordRules({
      keyword: keyword.value,
      page: query.value.page,
      page_size: query.value.page_size
    })
    list.value = data.list || []
    total.value = data.total || 0
  } catch (e) {
    ElMessage.error(e.message || '加载失败')
  } finally {
    loading.value = false
  }
}

function onSearch() {
  query.value.page = 1
  load()
}

function onReset() {
  keyword.value = ''
  query.value.page = 1
  load()
}

function onPageChange(page) {
  query.value.page = page
  load()
}

function openCreate() {
  editId.value = 0
  form.value = { keyword: '', ban_text: true, ban_audio: false, enabled: true, note: '' }
  dialogVisible.value = true
}

function openEdit(row) {
  editId.value = row.id
  form.value = {
    keyword: row.keyword || '',
    ban_text: !!row.ban_text,
    ban_audio: !!row.ban_audio,
    enabled: row.enabled !== false,
    note: row.note || ''
  }
  dialogVisible.value = true
}

async function save() {
  if (!form.value.keyword.trim()) {
    ElMessage.warning('请输入关键词')
    return
  }
  if (!form.value.ban_text && !form.value.ban_audio) {
    ElMessage.warning('请至少勾选一种拦截范围')
    return
  }
  const payload = {
    keyword: form.value.keyword.trim(),
    ban_text: !!form.value.ban_text,
    ban_audio: !!form.value.ban_audio,
    enabled: !!form.value.enabled,
    note: form.value.note.trim()
  }
  try {
    if (editId.value) {
      await adminApi.updateKeywordRule(editId.value, payload)
    } else {
      await adminApi.createKeywordRule(payload)
    }
    ElMessage.success('保存成功')
    dialogVisible.value = false
    load()
  } catch (e) {
    ElMessage.error(e.message || '保存失败')
  }
}

async function remove(id) {
  try {
    await ElMessageBox.confirm(`确认删除规则 #${id} 吗？`, '删除确认', { type: 'warning' })
    await adminApi.deleteKeywordRule(id)
    ElMessage.success('删除成功')
    load()
  } catch (e) {
    if (e === 'cancel') return
    ElMessage.error(e.message || '删除失败')
  }
}

onMounted(load)
</script>

